/**
 * Keyword-first dashboard (admin).
 * Money-keyword = uitgangspunt (i.p.v. website). Per keyword: zoekvolume, onze beste rank + welke
 * site die pakt, geschatte organische bezoekers (volume × CTR-positie), en de forecast-keten naar
 * leads → omzet (gekalibreerd op huidige leads/zichtbaarheid per niche) + de upside naar #1.
 * Data via /api/data/keyword-list (view seo_keyword_board).
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import AuthScreen from './shared/AuthScreen';
import { apiGet } from '../utils/apiClient';
import DashboardNav from './shared/DashboardNav';

const NL = (v) => '€' + Math.round(Number(v || 0)).toLocaleString('nl-NL');
const NUM = (v) => Number(v || 0).toLocaleString('nl-NL');
const NICHE_LABEL = { ontstoppen: 'ontstoppen', 'riool-ontstoppen': 'riool', ontstoppingsdienst: 'dienst', loodgieter: 'loodgieter', dakdekker: 'dakdekker' };
const NICHE_GROUP = (n) => (n === 'riool-ontstoppen' ? 'riool' : n === 'ontstoppingsdienst' ? 'dienst' : n);

const SORT_VAL = (r, col) => {
  switch (col) {
    case 'kw': return (r.keyword || '').toLowerCase();
    case 'vol': return Number(r.search_volume || 0);
    case 'pos': return r.best_pos == null ? 999 : Number(r.best_pos);
    case 'visits': return Number(r.est_visits_mnd || 0);
    case 'leads': return Number(r.est_leads_mnd || 0);
    case 'omzet': return Number(r.est_omzet_mnd || 0);
    case 'upside': return Number(r.upside_omzet_top1 || 0);
    default: return null;
  }
};
const DEFAULT_DIR = { kw: 'asc', vol: 'desc', pos: 'asc', visits: 'desc', leads: 'desc', omzet: 'desc', upside: 'desc' };

// Prioriteringssegmenten op basis van rankpositie (SEO best practice)
const STATUS_MATCH = (p, f) => {
  switch (f) {
    case 'verbeterbaar': return p !== 1;                       // alles behalve #1 (waar nog winst zit)
    case 'pos23': return p === 2 || p === 3;                   // bijna #1, afmaken
    case 'striking': return p != null && p >= 4 && p <= 10;    // striking distance, beste ROI
    case 'top10': return p != null && p >= 1 && p <= 10;       // pagina 1 (1-10)
    case 'page2': return p != null && p >= 11 && p <= 20;      // pagina 2, push naar pagina 1
    case 'buiten20': return p == null || p > 20;               // niet in top-20
    case 'top3': return p != null && p <= 3;                   // in top-3
    default: return true;
  }
};

const posCls = (p) => (p <= 3 ? 'good' : p <= 10 ? 'warn' : 'bad');

// Beweging-filter op de status uit seo_rank_changes
const MOVE_MATCH = (mv, f) => {
  if (!f) return true;
  const s = mv && mv.status;
  switch (f) {
    case 'stijgers': return s === 'gestegen';
    case 'dalers': return s === 'gedaald';
    case 'uit': return s === 'uit beeld gevallen';
    case 'nieuw': return s === 'nieuw in beeld';
    case 'bewegers': return s === 'gestegen' || s === 'gedaald' || s === 'uit beeld gevallen' || s === 'nieuw in beeld';
    default: return true;
  }
};

// Beweging t.o.v. de vorige rank-run (delta = pos_vorige - pos_nu; positief = gestegen)
function Delta({ mv }) {
  if (!mv) return null;
  const { delta, status } = mv;
  if (status === 'nieuw in beeld') return <span className="kw-dlt new" title="nieuw in beeld — we ranken hier voor het eerst">✦</span>;
  if (status === 'uit beeld gevallen') return <span className="kw-dlt out" title="uit beeld gevallen — buiten het gemeten venster gezakt">✗</span>;
  if (delta == null || status === 'eerste meting') return null;
  if (delta > 0) return <span className="kw-dlt up">▲{delta}</span>;
  if (delta < 0) return <span className="kw-dlt down">▼{Math.abs(delta)}</span>;
  return null; // gemeten, geen verandering
}

// Positie-verloop (laatste rank-runs). VASTE, gedeelde schaal i.p.v. per-keyword min/max, zodat de
// grafiekjes onderling vergelijkbaar zijn (Tufte small-multiples): #1 staat bovenaan en de diepte is
// log-samengedrukt (top-zwaar, zoals rank-trackers) — een betere positie ligt hoger, en de hoogte/
// helling betekent overal hetzelfde. Stijging = groene lijn omhoog, daling = rode lijn omlaag, geen
// mutatie = vlakke neutrale lijn op zijn echte hoogte. Punt = de huidige run.
const SPARK_CAP = 20;   // gemeten venster (cron-diepte 20); dieper clamt naar de vloer
function Trend({ hist }) {
  const v = (Array.isArray(hist) ? hist : []).filter((x) => x != null).map(Number);
  if (v.length < 2) return <span className="kw-mut">—</span>;
  const w = 58, h = 22, pad = 3;
  const frac = (p) => Math.log(Math.min(Math.max(p, 1), SPARK_CAP)) / Math.log(SPARK_CAP); // 0 (#1) .. 1 (#CAP)
  const px = (i) => (i / (v.length - 1)) * w;
  const py = (p) => pad + frac(p) * (h - 2 * pad);   // lager rangnummer → kleinere y → hoger
  const pts = v.map((p, i) => `${px(i).toFixed(1)},${py(p).toFixed(1)}`).join(' ');
  const net = v[v.length - 1] - v[0];   // <0 = rangnummer gedaald = positie verbeterd = gestegen
  const up = net < 0, down = net > 0;
  const col = up ? 'var(--color-success)' : down ? '#e11d48' : 'var(--color-neutral-300)';
  const lx = px(v.length - 1), ly = py(v[v.length - 1]);
  const tip = up ? `gestegen ${Math.abs(net)} ${Math.abs(net) === 1 ? 'positie' : 'posities'}`
    : down ? `gedaald ${Math.abs(net)} ${Math.abs(net) === 1 ? 'positie' : 'posities'}` : 'geen verandering';
  return (
    <svg className="kw-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={tip}>
      <title>{tip}</title>
      <polyline points={pts} fill="none" stroke={col} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx.toFixed(1)} cy={ly.toFixed(1)} r="1.9" fill={col} />
    </svg>
  );
}

function Sites({ sites, mv }) {
  const list = Array.isArray(sites) ? sites : [];
  if (!list.length) {
    if (mv && (mv.status === 'uit beeld gevallen' || mv.status === 'nieuw in beeld')) return <Delta mv={mv} />;
    return <span className="kw-mut">niet in top</span>;
  }
  const shown = list.slice(0, 6);
  const bestIdx = list.reduce((bi, s, i, arr) => (Number(s.rank) < Number(arr[bi].rank) ? i : bi), 0);
  return (
    <div className="kw-sites">
      {shown.map((s, i) => (
        <div className="kw-srow" key={`${s.domain}|${i}`}>
          <span className={`kw-pos ${posCls(s.rank)}`}>{s.rank}</span>
          <span className="kw-dltcell">{i === bestIdx ? <Delta mv={mv} /> : null}</span>
          <a className="kw-sdom" href={`https://${s.domain}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{s.domain}</a>
        </div>
      ))}
      {list.length > shown.length && <div className="kw-smore">+{list.length - shown.length} meer</div>}
    </div>
  );
}

const DIAG = {
  'on-page': { label: 'on-page', cls: 'onpage', tip: 'Wij overtreffen de top-3 op links — de rem zit in on-page/relevantie/lokaal, niet in autoriteit. Links kopen helpt hier niet.' },
  'link-gap': { label: 'links', cls: 'linkgap', tip: 'Concurrent boven ons heeft een sterker linkprofiel (vaak Trust Flow) — hier wél (hoogwaardige) links bouwen.' },
  'niet-in-serp': { label: 'buiten top-10', cls: 'oos', tip: 'Onze site staat niet in de opgehaalde top-10, dus geen eigen metrics in deze run.' },
};
function Diag({ d }) {
  if (!d) return <span className="kw-mut">—</span>;
  const x = DIAG[d] || { label: d, cls: '', tip: '' };
  return <span className={`kw-diag ${x.cls}`} title={x.tip}>{x.label}</span>;
}

const MCOLS = [['da', 'DA'], ['pa', 'PA'], ['tf', 'TF'], ['cf', 'CF'],
  ['ref_domains', 'RD'], ['ref_ips', 'IP'], ['backlinks', 'BL']];

function AuthDetail({ a, site, colSpan }) {
  const top3 = Array.isArray(a.top3) ? a.top3 : [];
  const maxOf = (k) => top3.reduce((m, c) => Math.max(m, Number(c[k] || 0)), 0);
  const inSerp = a.our_rank != null;
  const rowsData = [];
  if (inSerp) rowsData.push({ rank: a.our_rank, domain: site || 'onze site', isOurs: true, src: a.our_source,
    da: a.our_da, pa: a.our_pa, tf: a.our_tf, cf: a.our_cf,
    ref_domains: a.our_ref_domains, ref_ips: a.our_ref_ips, backlinks: a.our_backlinks });
  top3.forEach((c) => rowsData.push({ ...c, isOurs: false }));
  const cellCls = (v, k, isOurs) => 'r' + (isOurs && v != null ? (Number(v) >= maxOf(k) ? ' win' : ' lose') : '');
  return (
    <td colSpan={colSpan} className="kw-detail">
      <div className="kw-dwrap">
        {!inSerp && <div className="kw-mut" style={{ padding: '0 0 8px' }}>Onze site staat buiten de top-10 — geen eigen metrics in deze run.</div>}
        <div className="kw-mtxwrap">
          <table className="kw-mtx">
            <thead><tr><th>ONS vs top-3</th>{MCOLS.map(([k, l]) => <th key={k} className="r">{l}</th>)}</tr></thead>
            <tbody>
              {rowsData.map((c, i) => (
                <tr key={i} className={c.isOurs ? 'kw-mours' : ''}>
                  <td className="kw-mdom"><span className="kw-mrank">#{c.rank}</span> {c.domain}{c.isOurs && c.src === 'url_metrics' && <span className="kw-srctag" title="Onze metrics komen uit de homepage-meting (url-metrics), niet uit deze SERP — want we staan buiten de top-10">homepage</span>}</td>
                  {MCOLS.map(([k]) => <td key={k} className={cellCls(c[k], k, c.isOurs)}>{c[k] ?? '—'}</td>)}
                </tr>
              ))}
              {top3.length === 0 && <tr><td colSpan={MCOLS.length + 1} className="kw-mut">Geen echte concurrenten in de top-3 (alleen noise of eigen sites).</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="kw-dlegend">Groen = wij leiden de top-3 op die metric, rood = achter. RD = ref. domains, IP = ref. IP's, BL = backlinks. Bron: Mangools (Moz + Majestic).</div>
      </div>
    </td>
  );
}

export default function KeywordDashboard() {
  const auth = useAuth('admin');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [fNiche, setFNiche] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fMove, setFMove] = useState('');
  const [sortCol, setSortCol] = useState('upside');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExp = (k) => setExpanded((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    if (!auth.authenticated) return;
    setLoading(true);
    apiGet('/data/keyword-list')
      .then((d) => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch((e) => { setErr(String(e.message || e)); setLoading(false); });
  }, [auth.authenticated]);

  const view = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const ONTS_ALL = ['ontstoppen', 'riool-ontstoppen', 'ontstoppingsdienst'];
    let r = rows.filter((x) =>
      (!qq || (x.keyword || '').toLowerCase().includes(qq) || (x.city || '').toLowerCase().includes(qq))
      && (!fNiche || (fNiche === 'ontstoppen-all' ? ONTS_ALL.includes(x.niche) : NICHE_GROUP(x.niche) === fNiche))
      && STATUS_MATCH(x.best_pos, fStatus)
      && MOVE_MATCH(x.mv, fMove));
    const dir = sortDir === 'asc' ? 1 : -1;
    r = [...r].sort((a, b) => {
      const va = SORT_VAL(a, sortCol), vb = SORT_VAL(b, sortCol);
      if (typeof va === 'string') return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
    return r;
  }, [rows, q, fNiche, fStatus, fMove, sortCol, sortDir]);

  const mvSummary = useMemo(() => {
    let up = 0, down = 0, nw = 0, out = 0;
    for (const r of view) {
      const s = r.mv && r.mv.status;
      if (s === 'gestegen') up++;
      else if (s === 'gedaald') down++;
      else if (s === 'nieuw in beeld') nw++;
      else if (s === 'uit beeld gevallen') out++;
    }
    return { up, down, nw, out, any: up + down + nw + out };
  }, [view]);

  const kpis = useMemo(() => {
    const omzet = view.reduce((a, r) => a + Number(r.est_omzet_mnd || 0), 0);
    const upside = view.reduce((a, r) => a + Number(r.upside_omzet_top1 || 0), 0);
    const ranked = view.filter((r) => r.best_pos != null);
    const avgPos = ranked.length ? Math.round((ranked.reduce((a, r) => a + Number(r.best_pos), 0) / ranked.length) * 10) / 10 : null;
    const websites = new Set(view.map((r) => r.best_site).filter(Boolean)).size;
    return { omzet, upside, avgPos, n: view.length, websites };
  }, [view]);

  if (auth.loading) return <div className="kw"><style>{KW_CSS}</style><div className="kw-load">Laden…</div></div>;
  if (!auth.authenticated) return <AuthScreen auth={auth} title="LEAD MAGNATE" subtitle="Money-keywords" error={err} />;

  const toggle = (c) => { if (sortCol === c) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); else { setSortCol(c); setSortDir(DEFAULT_DIR[c] || 'desc'); } };
  const arr = (c) => (sortCol === c ? <span className="kw-sortarr">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span> : null);

  return (
    <div className="kw">
      <style>{KW_CSS}</style>
      <DashboardNav active="keywords" />
      <div className="kw-top"><div className="kw-top-in">
        <div className="kw-brand"><div className="kw-logo">LM</div><span className="kw-bt">LEAD MAGNATE</span><span className="kw-crumb">› Money-keywords</span></div>
      </div></div>

      <div className="kw-wrap">
        {err && <div className="kw-err">{err}</div>}

        <div className="kw-kpis">
          <div className="kw-kpi"><div className="kw-k">Money-keywords</div><div className="kw-v">{NUM(kpis.n)}</div><div className="kw-s">in beeld</div></div>
          <div className="kw-kpi"><div className="kw-k">Geschatte omzet</div><div className="kw-v">{NL(kpis.omzet)}</div><div className="kw-s">/mnd · organisch, model</div></div>
          <div className="kw-kpi"><div className="kw-k">Upside naar #1</div><div className="kw-v" style={{ color: 'var(--color-success)' }}>{NL(kpis.upside)}</div><div className="kw-s">/mnd extra mogelijk</div></div>
          <div className="kw-kpi"><div className="kw-k">Gem. positie</div><div className="kw-v">{kpis.avgPos != null ? `#${kpis.avgPos}` : '—'}</div><div className="kw-s">waar we ranken</div></div>
        </div>

        <div className="kw-mvstrip">
          <span className="kw-mvh">Beweging</span>
          {mvSummary.any > 0 ? (<>
            <span className="kw-mvi up">▲ {NUM(mvSummary.up)} gestegen</span>
            <span className="kw-mvi down">▼ {NUM(mvSummary.down)} gedaald</span>
            <span className="kw-mvi new">✦ {NUM(mvSummary.nw)} nieuw</span>
            <span className="kw-mvi out">✗ {NUM(mvSummary.out)} uit beeld</span>
            <span className="kw-mvsince">vs vorige rank-run</span>
          </>) : (
            <span className="kw-mvi muted">eerste meting — stijgers, dalers en uitvallers verschijnen vanaf de volgende rank-run</span>
          )}
        </div>

        <div className="kw-note-model">Schatting: est. bezoekers = zoekvolume × CTR, gesommeerd over álle eigen posities die we voor het keyword pakken; leads = bezoekers × empirisch gekalibreerde factor per niche (geijkt op werkelijke leads uit server-side tracking, 30d). De Conv.-kolom toont de echte bezoeker→lead conversie per niche. Niche-totalen kloppen op de werkelijkheid; per los keyword indicatief.</div>

        <div className="kw-filters">
          <div className="kw-search"><Search size={16} /><input placeholder="Zoek keyword of plaats…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <select className="kw-sel" value={fNiche} onChange={(e) => setFNiche(e.target.value)}>
            <option value="">Alle types</option>
            <option value="ontstoppen-all">Ontstoppen (alle types)</option>
            <option value="ontstoppen">— ontstoppen (kaal)</option><option value="riool">— riool ontstoppen</option>
            <option value="dienst">— ontstoppingsdienst</option>
            <option value="loodgieter">Loodgieter</option><option value="dakdekker">Dakdekker</option>
          </select>
          <select className="kw-sel" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
            <option value="">Status: alle</option>
            <option value="verbeterbaar">Verbeterbaar (excl. #1)</option>
            <option value="pos23">Bijna #1 (pos 2-3)</option>
            <option value="striking">Striking distance (4-10)</option>
            <option value="top10">Pagina 1 (1-10)</option>
            <option value="page2">Pagina 2 (11-20)</option>
            <option value="buiten20">Buiten top-20 (20+)</option>
            <option value="top3">In top-3</option>
          </select>
          <select className="kw-sel" value={fMove} onChange={(e) => setFMove(e.target.value)}>
            <option value="">Beweging: alle</option>
            <option value="bewegers">Alle bewegers</option>
            <option value="stijgers">Stijgers</option>
            <option value="dalers">Dalers</option>
            <option value="uit">Uit beeld gevallen</option>
            <option value="nieuw">Nieuw in beeld</option>
          </select>
          <span className="kw-count">{loading ? 'laden…' : `${NUM(view.length)} keywords · ${NUM(kpis.websites)} sites`}</span>
          <button type="button" className="kw-keybtn" onClick={() => setShowKey((v) => !v)} title="Kolom-uitleg">ⓘ kolommen</button>
        </div>
        {showKey && (
          <div className="kw-keypanel">
            {[['KW', 'Keyword'], ['Vol', 'Zoekvolume/mnd'], ['Pos', 'Onze posities + sites'], ['EV', 'Est. bezoekers/mnd'],
              ['CR', 'Conversie bezoeker→lead'], ['EL', 'Est. leads/mnd'], ['EO', 'Est. omzet/mnd'],
              ['Up', 'Upside naar #1'], ['Dx', 'Authority-diagnose']].map(([c, d]) => (
              <span key={c} className="kw-keyitem"><b>{c}</b> {d}</span>
            ))}
          </div>
        )}

        <div className="kw-panel">
          <div className="kw-scroll">
          <table className="kw-table">
            <thead><tr>
              <th className="kw-nosort r kw-idxh" title="Volgnummer in de huidige sortering/filter">#</th>
              <th className="kw-sortable" title="Keyword" onClick={() => toggle('kw')}>KW{arr('kw')}</th>
              <th className="kw-sortable r" title="Maandelijks zoekvolume" onClick={() => toggle('vol')}>Vol{arr('vol')}</th>
              <th className="kw-sortable" title="Onze rankende posities + sites; pijl = beweging t.o.v. de vorige rank-run" onClick={() => toggle('pos')}>Pos{arr('pos')}</th>
              <th className="kw-nosort" title="Positie-verloop (laatste rank-runs) — omhoog = beter">8wk</th>
              <th className="kw-sortable r" title="Est. visitors — zoekvolume × CTR, gesommeerd over al onze rankende posities" onClick={() => toggle('visits')}>EV{arr('visits')}</th>
              <th className="kw-nosort r" title="Conversie — echte bezoeker→lead per niche (server-side tracking, 30d)">CR</th>
              <th className="kw-sortable r" title="Est. leads per maand" onClick={() => toggle('leads')}>EL{arr('leads')}</th>
              <th className="kw-sortable r" title="Est. omzet per maand" onClick={() => toggle('omzet')}>EO{arr('omzet')}</th>
              <th className="kw-sortable r" title="Upside naar #1 per maand" onClick={() => toggle('upside')}>Up{arr('upside')}</th>
              <th className="kw-nosort c" title="Authority-diagnose: on-page = wij out-linken de top-3 (fix de pagina); links = concurrent heeft sterker linkprofiel (bouw links). Klik een rij voor de volledige metrics.">Dx</th>
            </tr></thead>
            <tbody>
              {view.slice(0, 3000).map((r, i) => {
                const key = `${r.keyword}|${r.best_site}`;
                const hasAuth = !!r.auth;
                const isOpen = expanded.has(key);
                return (
                <React.Fragment key={key}>
                <tr className={hasAuth ? 'kw-rowexp' : ''} onClick={hasAuth ? () => toggleExp(key) : undefined}>
                  <td className="r kw-tnum kw-idx">{i + 1}</td>
                  <td><div className="kw-kwcell">
                    {hasAuth && <span className="kw-caret">{isOpen ? '▾' : '▸'}</span>}
                    <span className="kw-kwtxt">{r.keyword}</span></div></td>
                  <td className="r kw-tnum">{NUM(r.search_volume)}</td>
                  <td><Sites sites={r.own_sites} mv={r.mv} /></td>
                  <td><Trend hist={r.hist} /></td>
                  <td className="r kw-tnum" title="zoekvolume × CTR over al onze posities">{NUM(r.est_visits_mnd)}</td>
                  <td className="r"><span className="kw-conv">{r.conversie_pct != null ? `${r.conversie_pct}%` : '—'}</span></td>
                  <td className="r kw-tnum">{Number(r.est_leads_mnd || 0).toLocaleString('nl-NL')}</td>
                  <td className="r kw-tnum" title={r.lead_prijs != null ? `€${Number(r.lead_prijs).toLocaleString('nl-NL')} per lead (klant-specifiek)` : ''}>{NL(r.est_omzet_mnd)}</td>
                  <td className="r"><span className="kw-upside">{NL(r.upside_omzet_top1)}</span></td>
                  <td className="c"><Diag d={r.auth?.diagnose} /></td>
                </tr>
                {isOpen && <tr className="kw-detrow"><AuthDetail a={r.auth} site={r.best_site} colSpan={11} /></tr>}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
          {!loading && view.length > 0 && <div className="kw-swipe">Veeg de tabel horizontaal voor alle kolommen →</div>}
          {!loading && view.length === 0 && <div className="kw-note">Geen keywords gevonden.</div>}
          {view.length > 3000 && <div className="kw-note">Eerste 3000 van {NUM(view.length)} getoond — verfijn met de filters.</div>}
        </div>
      </div>
    </div>
  );
}

const KW_CSS = `
.kw{font-family:var(--font-display);background:var(--surface-sunken);min-height:100vh;color:var(--color-neutral-800)}
.kw-load{padding:60px;text-align:center;color:var(--color-neutral-500)}
.kw-top{background:linear-gradient(135deg,var(--color-brand-600),var(--color-brand-800));color:#fff}
.kw-top-in{max-width:84rem;margin:0 auto;padding:14px 24px;display:flex;align-items:center;justify-content:space-between}
.kw-brand{display:flex;align-items:center;gap:13px}
.kw-logo{width:30px;height:30px;border-radius:7px;background:#fff;color:var(--color-brand-600);display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px}
.kw-bt{font-weight:800;font-size:15px;letter-spacing:-.01em}
.kw-crumb{font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:#fff;text-decoration:none}
.kw-wrap{max-width:84rem;margin:0 auto;padding:26px 24px}
.kw-err{background:var(--color-error-light);border:1px solid var(--color-error);color:var(--color-error);padding:10px 14px;border-radius:8px;margin-bottom:16px;font-size:13px}
.kw-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:14px}
@media(max-width:760px){.kw-kpis{grid-template-columns:repeat(2,1fr)}}
.kw-kpi{position:relative;background:var(--surface);border:1px solid var(--color-neutral-100);border-radius:8px;padding:16px 18px;overflow:hidden;text-align:center}
.kw-kpi::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--color-brand-600),var(--color-brand-400))}
.kw-kpi .kw-k{font-size:10.5px;font-weight:700;color:var(--color-neutral-500);text-transform:uppercase;letter-spacing:.1em}
.kw-kpi .kw-v{font-family:var(--font-mono);font-size:23px;font-weight:600;color:var(--color-neutral-900);margin-top:3px}
.kw-kpi .kw-s{font-size:11px;color:var(--color-neutral-400);font-weight:500;margin-top:1px}
.kw-note-model{font-size:11.5px;color:var(--color-neutral-400);margin:0 0 16px;line-height:1.5}
.kw-filters{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:16px}
.kw-search{flex:1;min-width:220px;position:relative;display:flex;align-items:center}
.kw-search svg{position:absolute;left:12px;color:var(--color-neutral-400)}
.kw-search input{width:100%;border:1px solid var(--color-neutral-200);border-radius:6px;padding:10px 14px 10px 36px;font-family:var(--font-display);font-size:14px;color:var(--color-neutral-800)}
.kw-search input:focus{outline:none;border-color:var(--color-brand-500);box-shadow:0 0 0 3px var(--color-brand-100)}
.kw-sel{appearance:none;background:var(--surface);border:1px solid var(--color-neutral-200);border-radius:6px;padding:10px 30px 10px 13px;font-family:var(--font-display);font-size:14px;font-weight:500;color:var(--color-neutral-700);cursor:pointer;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 11px center}
.kw-count{font-size:12px;color:var(--color-neutral-500);font-weight:600;margin-left:auto}
.kw-panel{background:var(--surface);border:1px solid var(--color-neutral-100);border-radius:8px}
.kw-table{width:100%;border-collapse:separate;border-spacing:0}
.kw-table th{position:sticky;top:0;z-index:3;background:linear-gradient(180deg,var(--color-neutral-50),var(--color-neutral-100));padding:11px 14px;font-size:11px;font-weight:700;color:var(--color-neutral-500);text-transform:uppercase;letter-spacing:.08em;text-align:left;border-bottom:2px solid var(--color-neutral-200);box-shadow:0 1px 0 var(--color-neutral-200);white-space:nowrap;cursor:pointer;user-select:none}
.kw-table th:first-child{border-top-left-radius:8px}.kw-table th:last-child{border-top-right-radius:8px}
.kw-table th.r,.kw-table td.r{text-align:right}
.kw-table th.c,.kw-table td.c{text-align:center}
.kw-table th:hover{color:var(--color-neutral-700)}
.kw-sortarr{color:var(--color-brand-600);font-size:10px}
.kw-table td{padding:9px 14px;font-size:13.5px;color:var(--color-neutral-700);border-bottom:1px solid var(--color-neutral-100);vertical-align:top}
.kw-table tbody tr:hover{background:var(--row-hover)}
.kw-table tbody tr:last-child td{border-bottom:none}
.kw-tnum{font-variant-numeric:tabular-nums}
.kw-idx{color:var(--color-neutral-400);font-size:12px;width:1%;white-space:nowrap}
.kw-idxh{width:1%}
.kw-kwcell{display:flex;align-items:center;gap:8px}
.kw-kwtxt{font-weight:600;color:var(--color-neutral-900)}
.kw-site{display:block;color:var(--color-neutral-400);font-size:11px;margin-top:1px}
.kw-sites{display:flex;flex-direction:column;gap:3px}
/* vaste raster-kolommen: positie | delta | domein. De delta-kolom is altijd gereserveerd (ook leeg),
   zodat de domeinen over alle rijen exact onder elkaar uitlijnen, met of zonder beweging. */
.kw-srow{display:grid;grid-template-columns:30px 30px 1fr;align-items:baseline;column-gap:7px;line-height:1.3}
.kw-srow .kw-pos{text-align:right}
.kw-dltcell{text-align:left;font-variant-numeric:tabular-nums;white-space:nowrap}
.kw-sdom{font-size:12px;color:var(--color-neutral-500);text-decoration:none;border-bottom:1px solid transparent}
.kw-sdom:hover{color:var(--color-brand-600);border-bottom-color:var(--color-brand-300)}
.kw-smore{font-size:11px;color:var(--color-neutral-400);padding-left:74px}
.kw-nosort{cursor:default!important}
.kw-conv{color:var(--color-neutral-500);font-size:12px;font-variant-numeric:tabular-nums}
.kw-chip{display:inline-flex;align-items:center;font-size:10px;font-weight:700;padding:2px 8px;border-radius:999px;white-space:nowrap}
.kw-chip.ontstoppen{background:#e0f7fa;color:#0e7490}.kw-chip.riool{background:#dbeafe;color:#1d4ed8}.kw-chip.dienst{background:#ede9fe;color:#6d28d9}.kw-chip.loodgieter{background:#dcfce7;color:#15803d}.kw-chip.dakdekker{background:#fee2de;color:var(--color-brand-700)}
.kw-pos{font-family:var(--font-mono);font-weight:700;font-size:13px}
.kw-pos.good{color:var(--color-success)}.kw-pos.warn{color:var(--color-warning)}.kw-pos.bad{color:var(--color-error)}
.kw-mut{color:var(--color-neutral-300);font-size:12px}
.kw-upside{font-weight:700;color:var(--color-success);font-variant-numeric:tabular-nums}
.kw-dlt{font-family:var(--font-mono);font-weight:700;font-size:11px;white-space:nowrap}
.kw-dlt.up{color:var(--color-success)}
.kw-dlt.down{color:#e11d48}
.kw-dlt.new{color:var(--color-brand-600);font-size:13px;font-weight:700;cursor:default}
.kw-dlt.out{color:#b45309;font-size:12px;font-weight:800;cursor:default}
.kw-spark{display:block;vertical-align:middle}
.kw-mvstrip{display:flex;align-items:center;gap:16px;flex-wrap:wrap;background:var(--surface);border:1px solid var(--color-neutral-100);border-radius:8px;padding:9px 16px;margin-bottom:14px;font-size:12.5px;font-weight:600;color:var(--color-neutral-600)}
.kw-mvh{font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--color-neutral-500)}
.kw-mvi{font-variant-numeric:tabular-nums}
.kw-mvi.up{color:var(--color-success)}.kw-mvi.down{color:#e11d48}.kw-mvi.new{color:var(--color-brand-600)}.kw-mvi.out{color:#b45309}.kw-mvi.muted{color:var(--color-neutral-400);font-weight:500}
.kw-mvsince{margin-left:auto;color:var(--color-neutral-400);font-weight:600;font-size:11.5px}
.kw-note{font-size:12px;color:var(--color-neutral-400);text-align:center;padding:16px}
.kw-caret{display:inline-block;width:11px;color:var(--color-neutral-400);font-size:10px;margin-right:1px}
.kw-rowexp{cursor:pointer}
.kw-diag{display:inline-block;font-size:10.5px;font-weight:700;padding:2px 9px;border-radius:999px;white-space:nowrap}
.kw-diag.onpage{background:#dcfce7;color:#15803d}
.kw-diag.linkgap{background:#fee2e2;color:#b91c1c}
.kw-diag.oos{background:var(--color-neutral-100);color:var(--color-neutral-500)}
.kw-detrow:hover{background:transparent!important}
.kw-detail{padding:0}
.kw-dwrap{padding:12px 16px 14px 34px;background:var(--color-neutral-50);border-bottom:1px solid var(--color-neutral-100)}
.kw-mtx{width:auto;border-collapse:collapse;font-size:12.5px;background:var(--surface);border:1px solid var(--color-neutral-100);border-radius:8px;overflow:hidden}
.kw-mtx th{position:static;top:auto;z-index:auto;background:var(--color-neutral-50);box-shadow:none;border:none;border-bottom:1px solid var(--color-neutral-200);text-transform:none;letter-spacing:0;color:var(--color-neutral-500);font-size:11px;font-weight:700;padding:6px 16px;cursor:default;white-space:nowrap}
.kw-mtx td{border:none;border-bottom:1px solid var(--color-neutral-100);padding:6px 16px;color:var(--color-neutral-700);font-variant-numeric:tabular-nums;white-space:nowrap}
.kw-mtx th.r,.kw-mtx td.r{text-align:right}
.kw-mtx tbody tr:last-child td{border-bottom:none}
.kw-mours td{font-weight:700;background:var(--row-selected)}
.kw-mours td.win{color:var(--color-success)}
.kw-mours td.lose{color:var(--color-error)}
.kw-mrank{color:var(--color-neutral-400);font-variant-numeric:tabular-nums;margin-right:4px}
.kw-srctag{display:inline-block;font-size:9px;font-weight:700;color:var(--color-neutral-500);background:var(--color-neutral-100);border-radius:4px;padding:1px 5px;margin-left:6px;vertical-align:middle}
.kw-dlegend{font-size:10.5px;color:var(--color-neutral-400);margin-top:7px}
.kw-keybtn{font-family:var(--font-display);font-size:11px;font-weight:600;color:var(--color-neutral-500);background:var(--surface);border:1px solid var(--color-neutral-200);border-radius:6px;padding:6px 10px;cursor:pointer}
.kw-keybtn:hover{color:var(--color-brand-600);border-color:var(--color-brand-300)}
.kw-keypanel{display:flex;flex-wrap:wrap;gap:6px 14px;margin:-6px 0 14px;padding:10px 12px;background:var(--color-neutral-50);border:1px solid var(--color-neutral-100);border-radius:8px}
.kw-keyitem{font-size:11.5px;color:var(--color-neutral-500)}
.kw-keyitem b{color:var(--color-neutral-800);font-weight:700;margin-right:3px}
.kw-cards{display:none}
.kw-mtxwrap{overflow-x:auto;-webkit-overflow-scrolling:touch}
.kw-card{border:1px solid var(--color-neutral-100);border-radius:8px;padding:8px 10px;margin-bottom:8px;background:var(--surface)}
.kw-card.us{border-color:var(--color-brand-300);background:var(--row-selected)}
.kw-cdom{display:flex;align-items:center;gap:6px;font-size:12.5px;font-weight:600;color:var(--color-neutral-800);margin-bottom:7px}
.kw-cdomn{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.kw-ustag{font-size:9px;font-weight:800;color:var(--color-brand-700);background:var(--color-brand-50);border:1px solid var(--color-brand-300);border-radius:4px;padding:1px 5px;margin-left:auto;flex:none}
.kw-cstats{display:flex;flex-wrap:wrap;gap:5px}
.kw-stat{font-size:11px;font-variant-numeric:tabular-nums;color:var(--color-neutral-600);background:var(--color-neutral-100);border-radius:5px;padding:2px 7px;white-space:nowrap}
.kw-stat.win{background:#dcfce7;color:#15803d;font-weight:700}
.kw-stat.lose{background:#fee2e2;color:#b91c1c;font-weight:700}
.kw-scroll{overflow-x:visible}
.kw-swipe{display:none}
@media(max-width:768px){
  /* uitklap-detail blijft een tabel (kolom-vergelijking), maar compact */
  .kw-mtx{font-size:11px}
  .kw-mtx th,.kw-mtx td{padding:5px 7px}
  .kw-mtx th:first-child,.kw-mtx td.kw-mdom{max-width:96px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .kw-mrank{margin-right:2px}
  .kw-kwcell .kw-chip{display:none}
  .kw-detrow td.kw-detail{position:sticky;left:0}
  .kw-dwrap{width:calc(100vw - 26px);box-sizing:border-box}
  .kw-wrap{padding:16px 12px}
  .kw-top-in{padding:12px 14px}
  .kw-bt{font-size:14px}
  .kw-crumb{font-size:11px}
  .kw-kpis{grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:12px}
  .kw-kpi{padding:12px 12px}
  .kw-kpi .kw-v{font-size:19px}
  .kw-note-model{font-size:11px;margin-bottom:12px}
  .kw-filters{gap:8px}
  .kw-search{min-width:0;flex:1 1 100%}
  .kw-sel{flex:1 1 calc(50% - 4px);padding:11px 28px 11px 12px}
  .kw-count{margin-left:0;flex:1 1 100%;text-align:right}
  /* horizontale tabel-scroll op mobiel (sticky header alleen op desktop) */
  .kw-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch;overscroll-behavior-x:contain}
  .kw-table{min-width:780px}
  .kw-table th{position:static;box-shadow:none}
  .kw-table td{padding:10px 12px}
  .kw-table th{padding:10px 12px}
  .kw-swipe{display:block;font-size:11px;color:var(--color-neutral-400);padding:8px 4px 2px;text-align:right}
  .kw-dwrap{padding:12px 12px 12px 16px}
}
/* landscape-telefoon (breder dan 768) — chip weg, website subtieler, scroll-vangnet zodat Dx past */
@media(max-width:1000px){
  .kw-kwcell .kw-chip{display:none}
  .kw-sdom{display:inline-block;max-width:108px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;vertical-align:bottom}
  .kw-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch}
}
`;
